import { useEffect, useMemo, useState } from 'react';
import { useMap, useMapEvent } from 'react-leaflet';
import { LatLng, LatLngBounds, LatLngBoundsLiteral } from 'leaflet';
import {
  AircraftMarkerType,
  FboMarkerType,
  HospitalMarkerType,
  HubMarkerType,
  MapFilterType,
  RouteMarkerType,
  RoutePolylineType,
  TxCenterMarkerType,
} from '@lp/features/RoutePlanner/types';
import { generateMarkers, generatePathRouting, getHubMarker } from '@lp/features/RoutePlanner/utils';
import { renderMapMarker } from '@lp/features/RoutePlanner/config';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { groupBy, uniqBy } from 'lodash-es';
import { OrganType } from '@/api/apis/case';
import {
  AircraftLegResource,
  AirportRouteLocation,
  AviaLegTypes,
  BaseLegResourceType,
  CustomAddressRouteLocation,
  GroundLegTypes,
  HubRouteLocation,
  LegLocationTypeEnum,
  RouteLocationType,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { RoutePolyline } from './RoutePolyline';
import { CustomLocationRouteMarker, FboMarker, ResourceRouteMarker } from './markers';

type ResourceMarkerType = {
  id: string;
  location: LatLng;
  type: MapFilterType;
};

type RouteMapProps = {
  organType: OrganType;
  donorHospitalMarker: HospitalMarkerType | null;
  txCenterMarker: TxCenterMarkerType | null;
  legs: (AviaLegTypes | GroundLegTypes)[];
  locations: RouteLocationType[];
  aircrafts?: BaseLegResourceType<AircraftLegResource>[];
  aircraftsMarkers?: AircraftMarkerType[];
};

export const RouteMap = ({
  donorHospitalMarker,
  txCenterMarker,
  organType,
  legs,
  locations,
  aircrafts,
  aircraftsMarkers,
}: RouteMapProps) => {
  const { ClinicalSpecialist: specialists, Surgeon: surgeons, OCSConsole: devices } = useRouteResources();
  const map = useMap();
  const [zoomLevel, setZoomLevel] = useState(map.getZoom());
  const [routes, setRoutes] = useState<RoutePolylineType[]>([]);
  const [fboMarkers, setFboMarkers] = useState<FboMarkerType[]>([]);
  const [customMarkers, setCustomMarkers] = useState<RouteMarkerType[]>([]);

  useMapEvent('zoom', () => {
    setZoomLevel(map.getZoom());
  });

  useEffect(() => {
    const init = async () => {
      const data = await generatePathRouting(legs, locations, aircrafts);
      const generatedFboMarkers = generateMarkers<FboMarkerType>(legs, LegLocationTypeEnum.airport, (locationId) => {
        const locationData = locations.find((l) => l.id === locationId) as AirportRouteLocation;

        return {
          id: locationData.id,
          type: SegmentTypeEnum.reposition,
          location: new LatLng(locationData.location.latitude, locationData.location.longitude),
          code: locationData.iata || locationData.icao,
          title: `${locationData.iata || locationData.icao} (${locationData.name})`,
        };
      });
      const generatedCustomMarkers = generateMarkers(legs, LegLocationTypeEnum.custom, (locationId) => {
        const locationData = locations.find((l) => l.id === locationId) as CustomAddressRouteLocation;

        return {
          id: locationData.id,
          location: new LatLng(locationData.location.latitude, locationData.location.longitude),
        };
      });

      setRoutes(data);
      setFboMarkers(generatedFboMarkers);
      setCustomMarkers(generatedCustomMarkers);

      const bounds = new LatLngBounds(data.map((i) => i.positions).flat() as LatLngBoundsLiteral);

      if (bounds.isValid()) {
        map.fitBounds(bounds);
      }
    };

    if (legs.length) {
      init();
    } else {
      setRoutes([]);
      setFboMarkers([]);
      setCustomMarkers([]);
    }
  }, [legs, aircrafts, map, locations]);

  const resourceMarkers = useMemo(() => {
    const csLocations = specialists.reduce<ResourceMarkerType[]>((acc, i) => {
      const location = i.data?.locationPreference === 'Home' ? i.data?.homeLocation : i.data?.baseLocation?.location;

      if (location) {
        return [
          ...acc,
          {
            id: location.id,
            location: new LatLng(location.latitude, location.longitude),
            type: MapFilterType.clinicalSpecialists,
          },
        ];
      }

      return acc;
    }, []);

    const sLocations = surgeons.reduce<ResourceMarkerType[]>((acc, i) => {
      const location = i.data?.locationPreference === 'Home' ? i.data?.homeLocation : i.data?.baseLocation?.location;

      if (location) {
        return [
          ...acc,
          {
            id: location.id,
            location: new LatLng(location.latitude, location.longitude),
            type: MapFilterType.surgeon,
          },
        ];
      }

      return acc;
    }, []);

    const devicesLocations = devices.reduce<ResourceMarkerType[]>((acc, i) => {
      const location = i.data?.hub?.location;

      if (location) {
        return [
          ...acc,
          {
            id: location.id,
            location: new LatLng(location.latitude, location.longitude),
            type: MapFilterType.ocsDevices,
          },
        ];
      }

      return acc;
    }, []);

    const csAndDevices = groupBy([...csLocations, ...devicesLocations], 'id');
    const formattedCsAndDevices = Object.keys(csAndDevices).map((i) => {
      if (i.length === 1) return csAndDevices[i];

      return {
        id: i,
        location: csAndDevices[i][0]?.location,
        type: csAndDevices[i].map((r) => r?.type),
      };
    });

    return uniqBy(
      [...sLocations, ...formattedCsAndDevices].filter((i): i is ResourceMarkerType => !!i),
      'id',
    );
  }, [specialists, surgeons, devices]);

  const hubMarkers = useMemo(() => {
    return uniqBy(
      legs
        .filter((l) => {
          return (
            l.departureLocation.locationType === LegLocationTypeEnum.hub ||
            l.arrivalLocation.locationType === LegLocationTypeEnum.hub
          );
        })
        .reduce<HubMarkerType[]>((acc, l) => {
          if (l.departureLocation.locationType === LegLocationTypeEnum.hub && l.departureLocation.locationId) {
            const locationData = locations.find((loc) => loc.id === l.departureLocation.locationId);
            const hubMarker = getHubMarker(locationData as HubRouteLocation);

            if (hubMarker) {
              return [...acc, hubMarker];
            }
          }

          if (l.arrivalLocation.locationType === LegLocationTypeEnum.hub && l.arrivalLocation.locationId) {
            const locationData = locations.find((loc) => loc.id === l.arrivalLocation.locationId);
            const hubMarker = getHubMarker(locationData as HubRouteLocation);

            if (hubMarker) {
              return [...acc, hubMarker];
            }
          }

          return acc;
        }, []),
      'id',
    );
  }, [locations, legs]);

  return (
    <>
      {resourceMarkers.map((marker) => (
        <ResourceRouteMarker key={marker.id} {...marker} />
      ))}
      {hubMarkers.map((marker) => renderMapMarker(marker, organType))}
      {donorHospitalMarker ? renderMapMarker(donorHospitalMarker, organType) : null}
      {txCenterMarker ? renderMapMarker(txCenterMarker, organType) : null}
      {zoomLevel >= 9 && customMarkers.map((data) => <CustomLocationRouteMarker key={data.id} {...data} />)}
      {fboMarkers.map((data) => (
        <FboMarker key={data.id} {...data} />
      ))}
      {routes.map((data) => (
        <RoutePolyline key={data.id} {...data} zoomLevel={zoomLevel} />
      ))}
      {aircraftsMarkers && aircraftsMarkers.map((marker) => renderMapMarker(marker, organType))}
    </>
  );
};
